// src/components/Settings/Layout/GetSubscription.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useMonetization } from "../../../../../hooks/useMonetization";
import {
  FEATURE_PRICING,
  FEATURE_NAMES,
  ALL_PAID_FEATURES,
  DATA_SOURCES,
  COIN_ECONOMICS,
} from "../../../../config/monetization";

const GetSubscription = () => {
  // ---- Existing subscription state ----
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [subscriptionCost, setSubscriptionCost] = useState(0);
  const [isAllInclusive,    setIsAllInclusive]    = useState(false);
  const [isSubscribed,      setIsSubscribed]      = useState(false);
  const [isAccountSafe,     setIsAccountSafe]     = useState(false);
  const [paymentMethod,     setPaymentMethod]     = useState("card");
  const [cardDetails, setCardDetails] = useState({
    name: "", cardNumber: "", expiryDate: "", cvv: "",
  });
  const [upiDetails, setUpiDetails] = useState({ upiId: "" });

  // ---- New monetization state ----
  const {
    defaultSubscription,
    setDefaultSubscription,
    dataSourceToggles,
    toggleDataSource,
    coinBalance,
  } = useMonetization();

  // ---- Existing load + calculate logic ----
  useEffect(() => { loadSelectedFeatures(); }, []);

  const loadSelectedFeatures = () => {
    try {
      const featuresStr = localStorage.getItem("features");
      if (featuresStr) {
        const features = JSON.parse(featuresStr);
        let codes = [];
        if (Array.isArray(features)) {
          codes = features
            .map((f) => (typeof f === "string" ? f : f.ID || f.featureCode || f.code || null))
            .filter(Boolean);
        }
        setSelectedFeatures(codes);
        calculateSubscriptionCost(codes);
      }
    } catch (err) { console.error(err); }
  };

  const calculateSubscriptionCost = (codes) => {
    const paid = codes.filter((c) => FEATURE_PRICING[c]);
    const allIn =
      ALL_PAID_FEATURES.every((pf) => paid.includes(pf)) &&
      paid.length >= ALL_PAID_FEATURES.length;
    if (allIn) {
      setIsAllInclusive(true);
      setSubscriptionCost(12);
    } else {
      setIsAllInclusive(false);
      setSubscriptionCost(
        paid.reduce((sum, c) => sum + (FEATURE_PRICING[c]?.max || 0), 0),
      );
    }
  };

  const processPaymentAndMergeFeatures = () => {
    try {
      const featuresNewStr = localStorage.getItem("features-new");
      if (!featuresNewStr) return false;
      const featuresNew = JSON.parse(featuresNewStr);
      let existing = JSON.parse(localStorage.getItem("features") || "[]")
        .map((f) => (typeof f === "string" ? f : f.ID || f.featureCode || f.code || null))
        .filter(Boolean);
      const updated = featuresNew.includes("ALL")
        ? ["ALL"]
        : [...new Set([...existing, ...featuresNew])];
      localStorage.setItem("features", JSON.stringify(updated));
      localStorage.removeItem("features-new");
      return true;
    } catch { return false; }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (paymentMethod === "card" &&
        (!cardDetails.name || !cardDetails.cardNumber || !cardDetails.expiryDate || !cardDetails.cvv)) {
      alert("❌ Please fill in all card details"); return;
    }
    if (paymentMethod === "upi" && !upiDetails.upiId) {
      alert("❌ Please enter your UPI ID"); return;
    }
    const mergeSuccess = processPaymentAndMergeFeatures();
    if (mergeSuccess) {
      alert(`✅ Payment Successful!\n\nAmount: $${subscriptionCost.toFixed(2)}/month\nPackage: ${isAllInclusive ? "ALL-INCLUSIVE" : "CUSTOM"}`);
      setCardDetails({ name: "", cardNumber: "", expiryDate: "", cvv: "" });
      setUpiDetails({ upiId: "" });
      setIsSubscribed(false);
      setIsAccountSafe(false);
      loadSelectedFeatures();
    } else {
      alert("❌ Payment succeeded but feature activation failed. Please contact support.");
    }
  };

  // ---- Render ----
  return (
    <div className="st-panel sub-root">
      <h2 className="st-panel-title">💳 Subscription & Monetization</h2>
      <p className="st-panel-subtitle">
        Manage your subscription plan, data contributions, and Kleido Coins.
      </p>

      {/* ============================================== */}
      {/* SECTION 1: Subscription Summary (unchanged)    */}
      {/* ============================================== */}
      <div className="sub-section" style={{background: "rgba(36,36,45,0.2)"}}>
        <h3 className="sub-section-title"> Subscription Summary</h3>
        {selectedFeatures.length === 0 ? (
          <div className="sub-empty-state">
            ⚠️ No features selected. Please visit the{" "}
            <Link to="/explore" className="sub-link">Explore page</Link> to select features.
          </div>
        ) : (
          <>
            <div className="sub-pricing">
              <div className="sub-price" style={{color: "white"}}>${subscriptionCost.toFixed(2)}</div>
              <div className="sub-period">/ month</div>
            </div>
            {isAllInclusive && (
              <div className="sub-savings">💰 Save up to $8/month with ALL package!</div>
            )}
            <div className="sub-features-box" style={{background: "none"}}>
              <h4 className="sub-features-title">Selected Features:</h4>
              <ul className="sub-features-list">
                {selectedFeatures.map((code) => (
                  <li key={code} className="sub-feature-item">
                    <span className="sub-feature-check" style={{color: "white" }}>✓</span>
                    <span className="sub-feature-name">{FEATURE_NAMES[code] || code}</span>
                    <span className={`sub-feature-price ${FEATURE_PRICING[code]?.min === 0 ? "sub-feature-price--free" : ""}`}>
                      {FEATURE_PRICING[code]?.label || "FREE"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>

      {/* ============================================== */}
      {/* SECTION 2: Default Subscription toggle (NEW)   */}
      {/* ============================================== */}
      <div className="sub-section">
        <h3 className="sub-section-title">⚙️ Subscription Preferences</h3>
        <label className="sub-checkbox-row">
          <input
            type="checkbox"
            className="sub-checkbox"
            checked={defaultSubscription}
            onChange={(e) => setDefaultSubscription(e.target.checked)}
          />
          <span className="sub-checkbox-label">
            <strong>Auto-subscribe to new features by default</strong>
            <div className="sub-checkbox-hint">
              When ON, any feature you add in the future is automatically
              subscribed and added to your monthly bill. You can always remove
              a feature later from the Explore page.
            </div>
          </span>
        </label>
      </div>

      {/* ============================================== */}
      {/* SECTION 3: Data Source Toggles (NEW)           */}
      {/* ============================================== */}
      <div className="sub-section">
        <h3 className="sub-section-title">🛡️ Data Used for Dataset Generation</h3>
        <p className="sub-section-help">
          Choose which categories of your data Kleido may use to build
          human intelliegence datasets. Anything OFF is excluded entirely — neither used
          for training nor shared. Sensitive categories are marked.
        </p>
        <p className="sub-section-help" >

        </p>

        <div className="sub-datasource-grid">
          {Object.values(DATA_SOURCES).map((src) => {
            const enabled = !!dataSourceToggles[src.id];
            return (
              <div
                key={src.id}
                className={`sub-datasource-card ${enabled ? "sub-datasource-card--on" : "sub-datasource-card--off"} ${src.sensitive ? "sub-datasource-card--sensitive" : ""}`}
              >
                <div className="sub-datasource-card-head">
                  <div className="sub-datasource-card-title" style={{display: "flex", flexDirection: "row"}}>
                    <label className="sub-toggle">
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={() => toggleDataSource(src.id)}
                      />
                      <span className="sub-toggle-slider" />
                    </label>
                    <div style={{marginLeft: 5}}>
                      {src.label}
                    </div>
                    <p>
                      {src.sensitive && (
                        <span className="sub-datasource-sensitive-badge" style={{marginLeft: 3}}>
                          (sensitive)
                        </span>
                      )}
                      </p>
                    <div className="sub-datasource-description" style={{marginLeft: 7}}>
                      - {src.description}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="sub-datasource-summary" style={{marginTop: 20}}>
          {Object.values(dataSourceToggles).filter(Boolean).length} of{" "}
          {Object.keys(DATA_SOURCES).length} categories enabled
        </div>
      </div>

      {/* ============================================== */}
      {/* SECTION 4: Kleido Coins (NEW)                 */}
      {/* ============================================== */}
      <div className="sub-section">
        <h3 className="sub-section-title">
          Kleido Coins ({COIN_ECONOMICS.CURRENCY_SYMBOL})
        </h3>

        <div className="sub-coins-balance" style={{display: "flex", flexDirection: "row" }}>
          <div className="sub-coins-amount">
            {coinBalance.toLocaleString()}
            <span className="sub-coins-symbol"> {COIN_ECONOMICS.CURRENCY_SYMBOL}</span>
          </div>
          <div className="sub-coins-usd">
            ≈ ${(coinBalance * 0.005).toFixed(2)} USD{" "}
            <span className="sub-coins-dynamic">(dynamic)</span>
          </div>
        </div>

        <div className="sub-coins-explain" style={{marginTop: 20 }}>
          <>
          </>
          <p>
            <strong>Kleido Coins</strong> are the platform's monetization
            currency. Their value is dynamic — it depends on company
            performance, your individual contribution, and demand for your
            content category.
          </p>
          <div style={{marginTop: 10 }}>
          <h4 className="sub-coins-factor-title">Coins are awarded based on:</h4>
          <div style={{marginLeft: 20 }}>
          <ul className="sub-coins-factor-list">
            <li>
              <span className="sub-coins-factor-name"> - Content Quality</span>
              <span className="sub-coins-factor-mult">
                ×{COIN_ECONOMICS.MULTIPLIERS.QUALITY}
              </span>
              <span className="sub-coins-factor-hint">
                originality, depth, signal-to-noise
              </span>
            </li>
            <li>
              <span className="sub-coins-factor-name"> - Attention Generated</span>
              <span className="sub-coins-factor-mult">
                ×{COIN_ECONOMICS.MULTIPLIERS.ATTENTION}
              </span>
              <span className="sub-coins-factor-hint">
                likes, shares, suggestions received
              </span>
            </li>
            <li>
              <span className="sub-coins-factor-name"> - Ad Revenue Pulled</span>
              <span className="sub-coins-factor-mult">
                ×{COIN_ECONOMICS.MULTIPLIERS.AD_REVENUE}
              </span>
              <span className="sub-coins-factor-hint">
                ads shown against your content
              </span>
            </li>
            <li>
              <span className="sub-coins-factor-name"> - Engagement Depth</span>
              <span className="sub-coins-factor-mult">
                ×{COIN_ECONOMICS.MULTIPLIERS.ENGAGEMENT}
              </span>
              <span className="sub-coins-factor-hint">
                watch time, replays, comment quality
              </span>
            </li>
            <li>
              <span className="sub-coins-factor-name"> - Community Value</span>
              <span className="sub-coins-factor-mult">
                ×{COIN_ECONOMICS.MULTIPLIERS.COMMUNITY}
              </span>
              <span className="sub-coins-factor-hint">
                Deluzon credibility, Klamity signal
              </span>
            </li>
          </ul>
          </div>
          </div>
        </div>
        <div style={{ marginTop: 20 }}>
          <p className="sub-coins-disclaimer">{COIN_ECONOMICS.DYNAMIC_NOTE}</p>
        </div>
      </div>

      {/* ============================================== */}
      {/* SECTION 5: Payment (unchanged)                 */}
      {/* ============================================== */}
      {selectedFeatures.length > 0 && subscriptionCost > 0 && (
        <label className="sub-checkbox-row">
          <input
            type="checkbox"
            checked={isSubscribed}
            onChange={() => setIsSubscribed(!isSubscribed)}
            className="sub-checkbox"
          />
          <span className="sub-checkbox-label">
            I want to subscribe to this service
          </span>
        </label>
      )}

      {isSubscribed && subscriptionCost > 0 && (
        <div className="sub-section sub-section--payment">
          <h3 className="sub-section-title">💳 Payment Method</h3>
          <div className="sub-payment-tabs">
            <button
              type="button"
              className={`sub-payment-tab ${paymentMethod === "card" ? "sub-payment-tab--active" : ""}`}
              onClick={() => setPaymentMethod("card")}
            >
              💳 Credit/Debit Card
            </button>
            <button
              type="button"
              className={`sub-payment-tab ${paymentMethod === "upi" ? "sub-payment-tab--active" : ""}`}
              onClick={() => setPaymentMethod("upi")}
            >
              📱 UPI
            </button>
          </div>

          {paymentMethod === "card" && (
            <form onSubmit={handleSubmit} className="sub-form">
              <div className="sub-form-grid-2">
                <div className="sub-form-group">
                  <label className="sub-form-label">Name on Card</label>
                  <input
                    type="text" placeholder="John Doe"
                    value={cardDetails.name}
                    onChange={(e) => setCardDetails({...cardDetails, name: e.target.value})}
                    className="sub-input" required
                  />
                </div>
                <div className="sub-form-group">
                  <label className="sub-form-label">Card Number</label>
                  <input
                    type="text" placeholder="1234 5678 9012 3456"
                    value={cardDetails.cardNumber}
                    onChange={(e) => setCardDetails({...cardDetails, cardNumber: e.target.value})}
                    className="sub-input" maxLength={19} required
                  />
                </div>
              </div>
              <div className="sub-form-grid-2">
                <div className="sub-form-group">
                  <label className="sub-form-label">Expiry (MM/YY)</label>
                  <input
                    type="text" placeholder="12/25"
                    value={cardDetails.expiryDate}
                    onChange={(e) => setCardDetails({...cardDetails, expiryDate: e.target.value})}
                    className="sub-input" maxLength={5} required
                  />
                </div>
                <div className="sub-form-group">
                  <label className="sub-form-label">CVV</label>
                  <input
                    type="text" placeholder="123"
                    value={cardDetails.cvv}
                    onChange={(e) => setCardDetails({...cardDetails, cvv: e.target.value})}
                    className="sub-input" maxLength={4} required
                  />
                </div>
              </div>
              <label className="sub-checkbox-row" style={{ marginTop: 20 }}>
                <input
                  type="checkbox" checked={isAccountSafe}
                  onChange={() => setIsAccountSafe(!isAccountSafe)}
                  className="sub-checkbox"
                />
                <span className="sub-checkbox-label">
                  Safeguard your Account <Link to="/" className="sub-link">(How?)</Link>
                </span>
              </label>
              <button type="submit" className="st-save-btn sub-submit-btn">
                💰 Pay ${subscriptionCost.toFixed(2)}/month
              </button>
              <div className="sub-security-badge">
                <span>🔒</span>
                <span>Secured with industry-standard encryption</span>
              </div>
            </form>
          )}

          {paymentMethod === "upi" && (
            <form onSubmit={handleSubmit} className="sub-form">
              <div className="sub-form-group">
                <label className="sub-form-label">UPI ID</label>
                <input
                  type="text" placeholder="yourname@upi"
                  value={upiDetails.upiId}
                  onChange={(e) => setUpiDetails({...upiDetails, upiId: e.target.value})}
                  className="sub-input" required
                />
              </div>
              <p className="sub-form-hint">e.g. username@paytm, username@googlepay</p>
              <label className="sub-checkbox-row" style={{ marginTop: 20 }}>
                <input
                  type="checkbox" checked={isAccountSafe}
                  onChange={() => setIsAccountSafe(!isAccountSafe)}
                  className="sub-checkbox"
                />
                <span className="sub-checkbox-label">
                  Safeguard your Account <Link to="/" className="sub-link">(How?)</Link>
                </span>
              </label>
              <button type="submit" className="st-save-btn sub-submit-btn">
                💰 Pay ${subscriptionCost.toFixed(2)}/month
              </button>
              <div className="sub-security-badge">
                <span>🔒</span>
                <span>Secured with industry-standard encryption</span>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ============================================== */}
      {/* SECTION 6: Enabled Features (unchanged)        */}
      {/* ============================================== */}
      {selectedFeatures.length > 0 && (
        <div className="sub-section sub-section--features" style={{background: "none"}}>
          <h3 className="sub-section-title"> Enabled Features</h3>
          <div className="sub-features-grid">
            {selectedFeatures.map((code) => (
              <div key={code} className="sub-feature-card" style={{ background: "rgba(36,36,45,0.2)", border: "1px solid rgba(234, 229, 234, 1)" }} >
                <div
                  className="sub-feature-card-icon"
                  style={{ background: "rgba(36,36,45,0.2)", border: "1px solid rgba(234, 229, 234, 1)" }}
                >
                  ✓
                </div>
                <div className="sub-feature-card-content">
                  <div className="sub-feature-card-name">
                    {FEATURE_NAMES[code] || code}
                  </div>
                  <div className="sub-feature-card-price">
                    {FEATURE_PRICING[code]?.label || "FREE"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GetSubscription;
